import React, { useEffect, useState } from "react";
import { IoClose, IoWarning, IoCalendar, IoPerson, IoCard, IoInformationCircle, IoConstruct } from "react-icons/io5";

const DomainRenew = () => {
  const [domainDetails, setDomainDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const currentDomain = window.location.hostname;

  useEffect(() => {
    const checkDomainStatus = async () => {
      try {
        const response = await fetch(`https://cyberspacedigital.in/apis/Domain_Renew.php?domain=${currentDomain}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        if (data.error) {
          setError(data.error);
          setShowPopup(false);
        } else {
          setDomainDetails(data);
          
          // ✅ Show maintenance if server is "off" or "disable"
          if (data.server === "off" || data.server === "disable") {
            setShowPopup(true);
            return;
          }
          
          // ✅ Show popup only if expired
          setShowPopup(data.expired === true);
        }
      } catch (error) {
        console.error("Error fetching domain status:", error);
        setError("Unable to verify domain status. Please try again later.");
        setShowPopup(false);
      } finally {
        setLoading(false);
      }
    };

    checkDomainStatus();
  }, [currentDomain]);

  // Don't render anything if loading or shouldn't show popup
  if (loading || !showPopup || error) {
    return null;
  }

  // ✅ Show Under Maintenance if server is "off" or "disable"
  if (domainDetails?.server === "off" || domainDetails?.server === "disable") {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
        <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden animate-fade-in">
          {/* Maintenance Header */}
          <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-6 text-white text-center">
            <div className="flex justify-center mb-3">
              <div className="p-3 bg-white/20 rounded-full">
                <IoConstruct className="text-3xl" />
              </div>
            </div>
            <h2 className="text-2xl font-bold mb-2">Under Maintenance</h2>
            <p className="opacity-90">Scheduled maintenance in progress</p>
          </div>

          {/* Maintenance Content */}
          <div className="p-6 text-center">
            <p className="text-gray-700 mb-4">
              We're currently performing scheduled maintenance for <strong>{domainDetails.domain}</strong>.
            </p>
            <p className="text-gray-600 text-sm">
              Please check back soon! We apologize for any inconvenience.
            </p>
            
            {domainDetails.notes && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <div className="flex items-start space-x-2">
                  <IoInformationCircle className="text-blue-500 text-lg mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 font-semibold text-sm mb-1">Important Notes</p>
                    <p className="text-blue-700 text-xs">{domainDetails.notes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <style jsx>{`
          @keyframes fade-in {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          .animate-fade-in {
            animation: fade-in 0.3s ease-out;
          }
        `}</style>
      </div>
    );
  }

  // If domain is active and not expired, render nothing
  if (!domainDetails || !domainDetails.expired) {
    return null;
  }

  // For expired domains or domains in renewal period
  const { domain, user_name, purchased_date, expiry_date, tier, notes, invoice_paynow_url, expired, days_until_expiry, can_close } = domainDetails;

  const isExpired = expired === true;
  const isWarningPeriod = days_until_expiry > 0 && days_until_expiry <= 5;
  const allowClose = can_close === true; // From server calculation

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = () => {
    if (isExpired) {
      return "from-red-500 to-red-600";
    } else if (isWarningPeriod) {
      return "from-orange-500 to-orange-600";
    }
    return "from-blue-500 to-blue-600";
  };

  const getStatusMessage = () => {
    if (isExpired) {
      return {
        title: "Domain Expired",
        subtitle: "Your domain subscription has expired",
        alert: "Urgent action required - service is suspended"
      };
    } else if (isWarningPeriod) {
      return {
        title: "Domain Expiring Soon",
        subtitle: `Expires in ${days_until_expiry} day${days_until_expiry !== 1 ? 's' : ''}`,
        alert: "Renew now to avoid service interruption"
      };
    }
    return {
      title: "Domain Status",
      subtitle: "Your domain subscription details",
      alert: ""
    };
  };

  const status = getStatusMessage();

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center p-2 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden animate-fade-in my-4">
        {/* Header - Sticky */}
        <div className={`bg-gradient-to-r ${getStatusColor()} p-4 text-white sticky top-0 z-10`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className="p-1.5 bg-white/20 rounded-lg flex-shrink-0">
                <IoWarning className="text-lg" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold truncate text-left">{status.title}</h2>
                <p className="text-white opacity-90 text-sm truncate">{status.subtitle}</p>
              </div>
            </div>
            {allowClose && (
              <button
                onClick={() => setShowPopup(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors duration-200 flex-shrink-0 ml-2"
              >
                <IoClose className="text-lg" />
              </button>
            )}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="max-h-[70vh] overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* Status Alert */}
            <div className={`border rounded-lg p-3 ${
              isExpired 
                ? "bg-red-50 border-red-200" 
                : "bg-orange-50 border-orange-200"
            }`}>
              <div className="flex items-start space-x-2">
                <IoWarning className={`text-lg flex-shrink-0 mt-0.5 ${
                  isExpired ? "text-red-500" : "text-orange-500"
                }`} />
                <div className="min-w-0 flex-1">
                  <p className={`font-semibold text-sm ${
                    isExpired ? "text-red-800" : "text-orange-800"
                  }`}>
                    Domain <span className="font-bold truncate">{domain}</span> {isExpired ? 'has expired' : 'is expiring soon'}
                  </p>
                  <p className={`text-xs mt-1 ${
                    isExpired ? "text-red-600" : "text-orange-600"
                  }`}>
                    {status.alert}
                  </p>
                </div>
              </div>
            </div>

            {/* Today's Date */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-1">
                <IoCalendar className="text-gray-500 text-sm" />
                <span className="text-xs font-semibold text-gray-600">Today's Date</span>
              </div>
              <p className="text-gray-800 font-medium text-sm">{new Date().toLocaleDateString()}</p>
            </div>

            {/* Domain Details Grid - Compact */}
            <div className="grid grid-cols-1 gap-2">
              {user_name && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <IoPerson className="text-blue-500 text-sm" />
                    <span className="text-xs font-semibold text-gray-600">User Name</span>
                  </div>
                  <p className="text-gray-800 font-medium text-sm truncate">{user_name}</p>
                </div>
              )}

              {tier && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <IoCard className="text-purple-500 text-sm" />
                    <span className="text-xs font-semibold text-gray-600">Plan Tier</span>
                  </div>
                  <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-semibold">
                    {tier}
                  </span>
                </div>
              )}

              {purchased_date && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <IoCalendar className="text-green-500 text-sm" />
                    <span className="text-xs font-semibold text-gray-600">Purchased Date</span>
                  </div>
                  <p className="text-gray-800 font-medium text-sm">{formatDate(purchased_date)}</p>
                </div>
              )}

              {expiry_date && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <IoWarning className={`text-sm ${
                      isExpired ? "text-red-500" : "text-orange-500"
                    }`} />
                    <span className="text-xs font-semibold text-gray-600">
                      {isExpired ? "Expired On" : "Expires On"}
                    </span>
                  </div>
                  <p className="text-gray-800 font-medium text-sm">{formatDate(expiry_date)}</p>
                  {!isExpired && days_until_expiry > 0 && (
                    <p className="text-orange-600 text-xs font-semibold mt-1">
                      {days_until_expiry} day{days_until_expiry !== 1 ? 's' : ''} remaining
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Notes Section */}
            {notes && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <IoInformationCircle className="text-blue-500 text-sm mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-blue-800 font-semibold text-xs mb-1">Important Notes</p>
                    <p className="text-blue-700 text-xs leading-relaxed">{notes}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className={`flex flex-col gap-2 pt-2 ${
              !allowClose ? 'justify-center' : ''
            }`}>
              {invoice_paynow_url && (
                <a
                  href={invoice_paynow_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl text-center flex items-center justify-center space-x-2 text-sm ${
                    allowClose ? '' : 'px-6'
                  }`}
                >
                  <IoCard className="text-base" />
                  <span>Pay Now & Renew</span>
                </a>
              )}
              
              {allowClose && (
                <button
                  onClick={() => setShowPopup(false)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-all duration-200 border border-gray-300 text-sm"
                >
                  Remind Me Later
                </button>
              )}
            </div>

            {/* Footer Note */}
            {isExpired && (
              <div className="text-center pt-3 border-t border-gray-200">
                <p className="text-red-600 text-xs font-semibold">
                  ⚠️ Service suspended until payment is completed
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Contact support if you need assistance
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add some custom animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        /* Custom scrollbar */
        .overflow-y-auto::-webkit-scrollbar {
          width: 4px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 2px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 2px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default DomainRenew;